import React, { useState } from 'react';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Plus, Trash2, GitBranch } from 'lucide-react';

/**
 * Advanced Condition Configuration Component
 * 
 * Provides an intuitive UI for building complex conditions with:
 * - Multiple comparison operators
 * - AND/OR logic
 * - Nested conditions
 * - Visual condition builder
 */
const AdvancedConditionConfig = ({ config, onChange }) => {
  const [conditions, setConditions] = useState(config?.conditions || []);
  const [conditionType, setConditionType] = useState(config?.conditionType || 'simple');

  // Available operators with descriptions
  const operators = [
    { value: 'equals', label: 'Equals (=)', description: 'Values are equal' },
    { value: 'not_equals', label: 'Not Equals (≠)', description: 'Values are not equal' },
    { value: 'contains', label: 'Contains', description: 'Text contains substring' },
    { value: 'not_contains', label: 'Does Not Contain', description: 'Text does not contain substring' },
    { value: 'starts_with', label: 'Starts With', description: 'Text starts with prefix' },
    { value: 'ends_with', label: 'Ends With', description: 'Text ends with suffix' },
    { value: 'greater_than', label: 'Greater Than (>)', description: 'Number is greater' },
    { value: 'less_than', label: 'Less Than (<)', description: 'Number is less' },
    { value: 'greater_or_equal', label: 'Greater or Equal (≥)', description: 'Number is greater or equal' },
    { value: 'less_or_equal', label: 'Less or Equal (≤)', description: 'Number is less or equal' },
    { value: 'is_empty', label: 'Is Empty', description: 'Value is empty or null' },
    { value: 'is_not_empty', label: 'Is Not Empty', description: 'Value has content' },
    { value: 'matches_regex', label: 'Matches Pattern', description: 'Matches regular expression' },
    { value: 'in_array', label: 'In Array', description: 'Value is in array' },
    { value: 'has_key', label: 'Has Key', description: 'Object has property' },
    { value: 'is_true', label: 'Is True', description: 'Value is truthy' },
    { value: 'is_false', label: 'Is False', description: 'Value is falsy' },
  ];

  const addCondition = () => {
    const newCondition = {
      id: `cond_${Date.now()}`,
      leftOperand: '',
      operator: 'equals',
      rightOperand: '',
      logicalOperator: 'AND'
    };
    const updated = [...conditions, newCondition];
    setConditions(updated);
    updateConfig(updated);
  };

  const removeCondition = (id) => {
    const updated = conditions.filter(c => c.id !== id);
    setConditions(updated);
    updateConfig(updated);
  };

  const updateCondition = (id, field, value) => {
    const updated = conditions.map(c => 
      c.id === id ? { ...c, [field]: value } : c
    );
    setConditions(updated);
    updateConfig(updated);
  };

  const updateConfig = (updatedConditions) => {
    onChange({
      ...config,
      conditionType,
      conditions: updatedConditions
    });
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <GitBranch className="w-5 h-5 text-amber-500" />
          <h3 className="text-lg font-semibold">Condition Builder</h3>
        </div>
        <Badge variant="outline" className="bg-amber-500/10 text-amber-600 border-amber-500/30">
          {conditions.length} condition{conditions.length !== 1 ? 's' : ''}
        </Badge>
      </div>

      {/* Condition Type Selector */}
      <div className="space-y-2">
        <Label>Condition Type</Label>
        <Select value={conditionType} onValueChange={(value) => {
          setConditionType(value);
          updateConfig(conditions);
        }}>
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="simple">Simple (All conditions in sequence)</SelectItem>
            <SelectItem value="advanced">Advanced (With nested groups)</SelectItem>
          </SelectContent>
        </Select>
        <p className="text-xs text-muted-foreground">
          Simple mode is easier to use. Advanced mode allows nested condition groups.
        </p>
      </div>

      {/* Conditions List */}
      <div className="space-y-3">
        {conditions.length === 0 ? (
          <Card className="border-dashed">
            <CardContent className="pt-6 pb-6 text-center">
              <p className="text-sm text-muted-foreground mb-3">
                No conditions yet. Add your first condition to get started.
              </p>
              <Button onClick={addCondition} size="sm" variant="outline">
                <Plus className="w-4 h-4 mr-2" />
                Add First Condition
              </Button>
            </CardContent>
          </Card>
        ) : (
          conditions.map((condition, index) => (
            <Card key={condition.id} className="border-amber-500/20">
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-sm font-medium">
                    Condition {index + 1}
                  </CardTitle>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => removeCondition(condition.id)}
                    className="h-8 w-8 p-0 text-destructive hover:text-destructive"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="space-y-3">
                {/* Left Operand */}
                <div className="space-y-2">
                  <Label className="text-xs">Left Value (Variable)</Label>
                  <Input
                    placeholder="{{trigger.email}} or {{step1.status}}"
                    value={condition.leftOperand}
                    onChange={(e) => updateCondition(condition.id, 'leftOperand', e.target.value)}
                    className="font-mono text-sm"
                  />
                  <p className="text-xs text-muted-foreground">
                    Use {`{{variable.path}}`} to reference data from previous steps
                  </p>
                </div>

                {/* Operator */}
                <div className="space-y-2">
                  <Label className="text-xs">Comparison</Label>
                  <Select
                    value={condition.operator}
                    onValueChange={(value) => updateCondition(condition.id, 'operator', value)}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {operators.map(op => (
                        <SelectItem key={op.value} value={op.value}>
                          <div className="flex flex-col">
                            <span className="font-medium">{op.label}</span>
                            <span className="text-xs text-muted-foreground">{op.description}</span>
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Right Operand (hide for unary operators) */}
                {!['is_empty', 'is_not_empty', 'is_true', 'is_false'].includes(condition.operator) && (
                  <div className="space-y-2">
                    <Label className="text-xs">Right Value (Compare To)</Label>
                    <Input
                      placeholder="Value to compare"
                      value={condition.rightOperand}
                      onChange={(e) => updateCondition(condition.id, 'rightOperand', e.target.value)}
                      className="font-mono text-sm"
                    />
                  </div>
                )}

                {/* Logical Operator (for chaining) */}
                {index < conditions.length - 1 && (
                  <div className="space-y-2 pt-2 border-t">
                    <Label className="text-xs">Then...</Label>
                    <div className="flex gap-2">
                      <Button
                        variant={condition.logicalOperator === 'AND' ? 'default' : 'outline'}
                        size="sm"
                        onClick={() => updateCondition(condition.id, 'logicalOperator', 'AND')}
                        className="flex-1"
                      >
                        AND (both must be true)
                      </Button>
                      <Button
                        variant={condition.logicalOperator === 'OR' ? 'default' : 'outline'}
                        size="sm"
                        onClick={() => updateCondition(condition.id, 'logicalOperator', 'OR')}
                        className="flex-1"
                      >
                        OR (either can be true)
                      </Button>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          ))
        )}
      </div>

      {/* Add Condition Button */}
      {conditions.length > 0 && (
        <Button onClick={addCondition} variant="outline" className="w-full">
          <Plus className="w-4 h-4 mr-2" />
          Add Another Condition
        </Button>
      )}

      {/* Condition Summary */}
      {conditions.length > 0 && (
        <Card className="bg-amber-500/5 border-amber-500/20">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium">Condition Summary</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm font-mono text-muted-foreground">
              {conditions.map((c, i) => (
                <span key={c.id}>
                  <span className="text-foreground">{c.leftOperand || '(empty)'}</span>
                  {' '}<span className="text-amber-600">{c.operator}</span>{' '}
                  {!['is_empty', 'is_not_empty', 'is_true', 'is_false'].includes(c.operator) && (
                    <span className="text-foreground">{c.rightOperand || '(empty)'}</span>
                  )}
                  {i < conditions.length - 1 && (
                    <span className="text-primary font-bold"> {c.logicalOperator} </span>
                  )}
                </span>
              ))}
            </p>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default AdvancedConditionConfig;
